import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Modal, Form, Input, Button, Select, Tag } from "antd";
import {
  HomeOutlined,
  CloseOutlined,
  SaveOutlined,
  AppstoreOutlined,
} from "@ant-design/icons";
import { viewCategoryList } from "../../Redux/Actions/categoryActions";
import "./addAndUpdateVrShootRentalPlan.css";

const { Option } = Select;

const VrShootRentalPlanEditModal = ({ visible, onClose, planData, onSave }) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();

  const [parentCategory, setParentCategory] = useState(null);
  const [parentCategories, setParentCategories] = useState([]);
  const [parentCategoriesLoading, setParentCategoriesLoading] = useState(false);

  const [subCategories, setSubCategories] = useState([]);
  const [subCategoriesLoading, setSubCategoriesLoading] = useState(false);

  const [resolvingEditCategory, setResolvingEditCategory] = useState(false);

  const mergeKnownOption = (list, known) => {
    if (!known?._id) return list;
    const exists = list.some((item) => item._id === known._id);
    return exists ? list : [known, ...list];
  };

  useEffect(() => {
    if (visible) {
      setParentCategoriesLoading(true);
      dispatch(viewCategoryList({ page: 1, limit: 100 }))
        .then((res) => {
          setParentCategories(res?.data?.data || res?.data || []);
        })
        .finally(() => setParentCategoriesLoading(false));
    }
  }, [visible, dispatch]);

  useEffect(() => {
    if (!parentCategory) {
      setSubCategories([]);
      return;
    }
    setSubCategoriesLoading(true);
    dispatch(viewCategoryList({ page: 1, limit: 100, category_id: parentCategory }))
      .then((res) => {
        const list = res?.data?.data || res?.data || [];
        if (planData?._id && planData?.category) {
          const knownSub =
            typeof planData.category === "object"
              ? { _id: planData.category._id, name: planData.category.name }
              : null;
          setSubCategories(mergeKnownOption(list, knownSub));
        } else {
          setSubCategories(list);
        }
      })
      .finally(() => setSubCategoriesLoading(false));
  }, [parentCategory, dispatch, planData]);

  useEffect(() => {
    const resolveEditCategory = async () => {
      if (!visible || !planData) return;

      const rawCategory = planData.category;
      const subId = typeof rawCategory === "object" ? rawCategory?._id : rawCategory;
      const subName = typeof rawCategory === "object" ? rawCategory?.name : undefined;

      // If planData already carries the parent id/object directly, use it.
      let parentId =
        (typeof rawCategory === "object" &&
          (rawCategory?.category_id?._id ||
            rawCategory?.category_id ||
            rawCategory?.parent_id?._id ||
            rawCategory?.parent_id)) ||
        null;
      let parentName =
        typeof rawCategory === "object"
          ? rawCategory?.category_id?.name || rawCategory?.parent_id?.name
          : undefined;

      // Otherwise, fetch the sub-category's own record to find its parent.
      if (!parentId && subId) {
        setResolvingEditCategory(true);
        try {
          const res = await dispatch(viewCategoryList({ id: subId }));
          const record = res?.data?.data?.[0] || res?.data?.data || res?.data;
          parentId =
            record?.category_id?._id ||
            record?.category_id ||
            record?.parent_id?._id ||
            record?.parent_id ||
            null;
          parentName = record?.category_id?.name || record?.parent_id?.name;
        } finally {
          setResolvingEditCategory(false);
        }
      }

      if (parentId) {
        setParentCategories((prev) =>
          mergeKnownOption(prev, { _id: parentId, name: parentName || parentId })
        );
      }
      setParentCategory(parentId);

      form.setFieldsValue({
        parent_category: parentId,
        category: subId,
        amount: planData.amount,
        cutPrice: planData.cutPrice,
        no_of_bedroom: planData.no_of_bedroom,
        status: planData.status || "active",
      });

      if (subId) {
        setSubCategories((prev) => mergeKnownOption(prev, { _id: subId, name: subName || subId }));
      }
    };

    if (visible && planData) {
      resolveEditCategory();
    } else if (visible) {
      form.resetFields();
      setParentCategory(null);
      setParentCategories([]);
      setSubCategories([]);
    }
  }, [visible, planData, form, dispatch]);

  const handleParentChange = (value) => {
    setParentCategory(value);
    form.setFieldsValue({ category: undefined });
  };

  const handleParentClear = () => {
    setParentCategory(null);
    setSubCategories([]);
    form.setFieldsValue({ parent_category: undefined, category: undefined });
  };

  const handleNumberInputWheel = (e) => {
    e.target.blur();
  };

  const handleFinish = (values) => {
    const { parent_category, ...rest } = values;

    onSave({
      ...rest,
      amount: rest.amount !== undefined && rest.amount !== "" ? Number(rest.amount) : rest.amount,
      cutPrice:
        rest.cutPrice !== undefined && rest.cutPrice !== "" ? Number(rest.cutPrice) : undefined,
      no_of_bedroom:
        rest.no_of_bedroom !== undefined && rest.no_of_bedroom !== ""
          ? Number(rest.no_of_bedroom)
          : rest.no_of_bedroom,
    });
  };

  const modalTitle = (
    <div className="vsrp-edit-modal-header-content">
      <div className="vsrp-edit-header-icon-wrap">
        <HomeOutlined style={{ color: "white", fontSize: 18 }} />
      </div>
      <div className="vsrp-edit-header-text">
        <span className="vsrp-edit-header-title">
          {planData?._id ? "Edit VR Shoot Rental Plan" : "Create VR Shoot Rental Plan"}
        </span>
        <span className="vsrp-edit-header-subtitle">
          {planData?._id ? "Update plan details below" : "Fill in the details to create a plan"}
        </span>
      </div>
    </div>
  );

  return (
    <Modal
      title={modalTitle}
      open={visible}
      onCancel={onClose}
      closeIcon={<CloseOutlined style={{ color: "white", fontSize: 16 }} />}
      footer={null}
      destroyOnClose
      width={560}
      className="vsrp-edit-modal-override"
      styles={{
        header: {
          background: "#070C18",
          borderRadius: "14px 14px 0 0",
          padding: "20px 24px",
          borderBottom: "none",
        },
        body: {
          padding: "0",
          background: "#f4f6fb",
        },
      }}
    >
      <div className="vsrp-edit-modal-form">
        <Form form={form} layout="vertical" onFinish={handleFinish}>

          <div className="vsrp-section">
            <div className="vsrp-section-label">
              <AppstoreOutlined className="vsrp-section-icon" />
              <span>Plan Details</span>
            </div>

            <div className="vsrp-edit-grid">
              <Form.Item
                label="Parent Category"
                name="parent_category"
                rules={[{ required: true, message: "Please select a parent category" }]}
              >
                <Select
                  size="large"
                  placeholder="Select parent category"
                  className="vsrp-edit-input-override"
                  showSearch
                  allowClear
                  optionFilterProp="children"
                  loading={parentCategoriesLoading || resolvingEditCategory}
                  onChange={handleParentChange}
                  onClear={handleParentClear}
                >
                  {parentCategories.map((cat) => (
                    <Option key={cat._id} value={cat._id}>
                      {cat.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                label="Sub Category"
                name="category"
                rules={[{ required: true, message: "Please select a sub category" }]}
              >
                <Select
                  size="large"
                  placeholder={parentCategory ? "Select sub category" : "Select parent category first"}
                  className="vsrp-edit-input-override"
                  showSearch
                  allowClear
                  optionFilterProp="children"
                  disabled={!parentCategory}
                  loading={subCategoriesLoading || resolvingEditCategory}
                  notFoundContent={parentCategory ? "No sub-categories found" : null}
                >
                  {subCategories.map((sub) => (
                    <Option key={sub._id} value={sub._id}>
                      {sub.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </div>

            <Form.Item
              label="No. of Bedrooms"
              name="no_of_bedroom"
              rules={[
                {
                  validator: (_, value) =>
                    value === undefined || value === "" || (!isNaN(value) && Number(value) >= 0)
                      ? Promise.resolve()
                      : Promise.reject(new Error("Must be a valid number")),
                },
              ]}
            >
              <Input
                type="number"
                size="large"
                placeholder="e.g. 2"
                className="vsrp-edit-input-override"
                onWheel={handleNumberInputWheel}
              />
            </Form.Item>

            <div className="vsrp-edit-grid">
              <Form.Item
                label="Amount"
                name="amount"
                rules={[
                  { required: true, message: "Please input amount" },
                  {
                    validator: (_, value) =>
                      value !== undefined && value !== "" && !isNaN(value) && Number(value) > 0
                        ? Promise.resolve()
                        : Promise.reject(new Error("Amount must be a valid positive number")),
                  },
                ]}
              >
                <Input
                  type="number"
                  size="large"
                  placeholder="e.g. 5000"
                  className="vsrp-edit-input-override"
                  onWheel={handleNumberInputWheel}
                />
              </Form.Item>

              <Form.Item
                label="Cut Price"
                name="cutPrice"
                rules={[
                  {
                    validator: (_, value) =>
                      value === undefined || value === "" || (!isNaN(value) && Number(value) >= 0)
                        ? Promise.resolve()
                        : Promise.reject(new Error("Cut price must be a valid number")),
                  },
                ]}
              >
                <Input
                  type="number"
                  size="large"
                  placeholder="e.g. 7000"
                  className="vsrp-edit-input-override"
                  onWheel={handleNumberInputWheel}
                />
              </Form.Item>
            </div>

            {planData?._id && (
              <Form.Item label="Status" name="status" initialValue="active">
                <Select size="large" className="vsrp-edit-input-override">
                  <Option value="active">
                    <Tag color="green" style={{ margin: 0 }}>Active</Tag>
                  </Option>
                  <Option value="inactive">
                    <Tag color="red" style={{ margin: 0 }}>Inactive</Tag>
                  </Option>
                </Select>
              </Form.Item>
            )}
          </div>

          <div className="vsrp-footer">
            <Button
              size="large"
              onClick={onClose}
              className="vsrp-edit-btn vsrp-edit-btn-cancel"
            >
              Cancel
            </Button>
            <Button
              htmlType="submit"
              size="large"
              icon={<SaveOutlined />}
              className="vsrp-edit-btn vsrp-edit-btn-primary"
            >
              <span className="vsrp-update-btn">
                {planData?._id ? "Update Plan" : "Create Plan"}
              </span>
            </Button>
          </div>

        </Form>
      </div>
    </Modal>
  );
};

export default VrShootRentalPlanEditModal;